import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import TextareaAutosize from 'react-textarea-autosize';
import { FaTimes, FaSpinner } from 'react-icons/fa';
import { rootUrl } from '../../../config/config';
import {
    applyCostChange,
    applyPriceChange,
    applyMarginChange,
    applyMarkupChange,
    applyVatChange
} from '../carPartsPricing';
import { formatMoney } from '../owners/formatMoney';
import SerialNumbersField from './SerialNumbersField';
import CompatibleVehiclesEditor from './CompatibleVehiclesEditor';
import PartSerialsPanel from './PartSerialsPanel';
import { selectPortalProps, inputClass, numberInputClass, labelClass, formatQty } from './partsUi';

const textareaClass = 'w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:border-[#FF5733] focus:ring-1 focus:ring-[#FF5733] resize-none';

const formFromPart = (part) => ({
    part_name: part?.part_name || '',
    part_number: part?.part_number || '',
    manufacturer: part?.manufacturer || '',
    category: part?.category || '',
    vat_id: part?.vat_id ?? 1,
    cost_excl: part?.cost_excl ?? '',
    cost_incl: part?.cost_incl ?? '',
    price_excl: part?.price_excl ?? '',
    price_incl: part?.price_incl ?? '',
    quantity: part?.quantity ?? '0',
    margin_percent: part?.margin_percent ?? '0',
    markup_percent: part?.markup_percent ?? '0',
    min_quantity: part?.min_quantity ?? '0',
    max_quantity: part?.max_quantity ?? '0',
    compatibility_notes: part?.compatibility_notes || '',
    image_url: part?.image_url || '',
    status: part?.status || 'ACTIVE',
    notes: part?.notes || '',
    has_serial: part?.has_serial === true,
    was_serial: part?.has_serial === true,
    serials_pending: part?.serials_pending === true,
    serials: []
});

const isMoney = (value) => String(value ?? '').trim() !== '' && !isNaN(Number(value)) && Number(value) >= 0;

const Section = ({ title, hint, children }) => (
    <section className="space-y-4">
        <div>
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#2a2e30]">{title}</h3>
            {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
        </div>
        {children}
    </section>
);

const FieldError = ({ message }) => (message ? <p className="text-xs text-red-500 mt-1">{message}</p> : null);

/**
 * Slide-over panel for adding or editing a car part (full screen on phones).
 * part: the part to edit, or null to add a new one.
 */
export default function PartFormDrawer({ open, part, referenceData, currentUsername, onClose, onSaved, onSerialsChanged }) {
    const { manufacturers, categories, statuses, vatRates, vehicleMakes } = referenceData;
    const editingId = part?.part_id || null;

    const [form, setForm] = useState(formFromPart(part));
    const [vehicles, setVehicles] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitError, setSubmitError] = useState('');
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('details');

    useEffect(() => {
        if (!open) return;
        setActiveTab(part?.has_serial && part?.serials_pending ? 'serials' : 'details');
        setForm(formFromPart(part));
        setVehicles([]);
        setFieldErrors({});
        setSubmitError('');
        if (!part?.part_id) return;
        const loadVehicles = async () => {
            try {
                const token = localStorage.getItem('authToken') || 'default-token';
                const res = await axios.get(`${rootUrl}/car_parts/${part.part_id}/compatibility`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setVehicles(res.data.data || []);
            } catch (err) {
                console.error('Failed to load compatibility:', err);
            }
        };
        loadVehicles();
    }, [open, part]);

    // Lock page scroll and close on Escape while the panel is open
    useEffect(() => {
        if (!open) return undefined;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e) => {
            if (e.key === 'Escape' && !saving) onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = previous;
            window.removeEventListener('keydown', onKey);
        };
    }, [open, saving, onClose]);

    if (!open) return null;

    const vatRate = (() => {
        const v = vatRates.find((r) => Number(r.vat_id) === Number(form.vat_id));
        return v ? Number(v.vat_rate) : 0;
    })();
    const selectedVat = vatRates.find((r) => Number(r.vat_id) === Number(form.vat_id));

    // Turning serial tracking on for a part with stock requires a serial for every unit on hand
    const needsOpeningSerials = form.has_serial && !form.was_serial && Number(form.quantity) > 0;
    const serialLocked = form.was_serial && Number(form.quantity) > 0;
    const missingOpeningSerials = needsOpeningSerials ? Math.max((parseInt(form.quantity, 10) || 0) - form.serials.length, 0) : 0;
    // In-stock serials get their own tab, only for parts already tracked by serial
    const showSerialsTab = Boolean(editingId && form.was_serial);

    const setField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (fieldErrors[field]) setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    };
    const setPricing = (fields, patch) => {
        setForm((prev) => ({ ...prev, ...patch(prev) }));
        setFieldErrors((prev) => {
            const next = { ...prev };
            fields.forEach((f) => delete next[f]);
            return next;
        });
    };

    const validate = () => {
        const errors = {};
        if (!form.part_name.trim()) errors.part_name = 'Enter the part name';
        if (!form.part_number.trim()) errors.part_number = 'Enter the part number';
        if (!form.manufacturer.trim()) errors.manufacturer = 'Select a manufacturer';
        if (!form.category.trim()) errors.category = 'Select a category';
        if (!isMoney(form.cost_excl)) errors.cost_excl = 'Enter a cost of 0 or more';
        if (!isMoney(form.cost_incl)) errors.cost_incl = 'Enter a cost of 0 or more';
        if (!isMoney(form.price_excl)) errors.price_excl = 'Enter a price of 0 or more';
        if (!isMoney(form.price_incl)) errors.price_incl = 'Enter a price of 0 or more';
        if (isNaN(Number(form.margin_percent)) || Number(form.margin_percent) < 0 || Number(form.margin_percent) >= 100) errors.margin_percent = 'Margin must be from 0 to below 100';
        if (isNaN(Number(form.markup_percent)) || Number(form.markup_percent) < 0) errors.markup_percent = 'Markup cannot be negative';
        if (isNaN(Number(form.min_quantity)) || Number(form.min_quantity) < 0) errors.min_quantity = 'Minimum cannot be negative';
        if (isNaN(Number(form.max_quantity)) || Number(form.max_quantity) < 0) errors.max_quantity = 'Maximum cannot be negative';
        if (!errors.min_quantity && !errors.max_quantity && Number(form.max_quantity || 0) > 0 && Number(form.min_quantity || 0) > Number(form.max_quantity || 0)) {
            errors.min_quantity = 'Minimum cannot be more than the maximum';
        }
        if (needsOpeningSerials) {
            // Serials can be left out for now: the part is then blocked until they're added
            const onHand = parseInt(form.quantity, 10) || 0;
            if (!Number.isInteger(Number(form.quantity))) {
                errors.serials = 'Serial-tracked parts can only be held in whole units';
            } else if (form.serials.length > onHand) {
                errors.serials = `Only ${formatQty(onHand)} unit(s) are on hand: enter at most ${formatQty(onHand)} serial number(s) (got ${form.serials.length})`;
            } else if (new Set(form.serials).size !== form.serials.length) {
                errors.serials = 'Each serial number can only be entered once';
            }
        }
        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = validate();
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) {
            setActiveTab('details');
            setSubmitError(`Fix the ${Object.keys(errors).length} highlighted field(s) before saving`);
            return;
        }
        setSubmitError('');
        setSaving(true);
        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            const headers = { Authorization: `Bearer ${token}` };
            const payload = {
                part_name: form.part_name.trim(),
                part_number: form.part_number.trim(),
                manufacturer: form.manufacturer,
                category: form.category,
                vat_id: Number(form.vat_id),
                cost_excl: Number(form.cost_excl),
                cost_incl: Number(form.cost_incl),
                price_excl: Number(form.price_excl),
                price_incl: Number(form.price_incl),
                quantity: Number(form.quantity),
                margin_percent: Number(form.margin_percent || 0),
                markup_percent: Number(form.markup_percent || 0),
                min_quantity: Number(form.min_quantity || 0),
                max_quantity: Number(form.max_quantity || 0),
                compatibility_notes: form.compatibility_notes,
                image_url: form.image_url,
                status: form.status,
                notes: form.notes,
                has_serial: form.has_serial
            };

            let result;
            if (editingId) {
                result = await axios.put(
                    `${rootUrl}/car_parts/${editingId}`,
                    { ...payload, edit_by: currentUsername, serials: needsOpeningSerials ? form.serials : [] },
                    { headers }
                );
            } else {
                result = await axios.post(`${rootUrl}/car_parts`, { ...payload, input_by: currentUsername }, { headers });
            }

            const partId = editingId || result.data.data?.part_id;
            if (partId) {
                for (const vehicle of vehicles.filter((v) => !v.id)) {
                    await axios.post(
                        `${rootUrl}/car_parts/${partId}/compatibility`,
                        {
                            vehicle_make: vehicle.vehicle_make,
                            vehicle_model: vehicle.vehicle_model,
                            vehicle_year_start: vehicle.vehicle_year_start || null,
                            vehicle_year_end: vehicle.vehicle_year_end || null,
                            notes: vehicle.notes || ''
                        },
                        { headers }
                    );
                }
            }

            const savedMessage = editingId ? `${payload.part_name} updated` : `${payload.part_name} added`;
            onSaved(missingOpeningSerials > 0
                ? `${savedMessage}. ${missingOpeningSerials} unit(s) still need serial numbers: the part is blocked until they are added on its Serial Numbers tab.`
                : savedMessage);
        } catch (err) {
            console.error(err);
            const apiData = err?.response?.data;
            let msg = editingId ? 'Failed to update the part.' : 'Failed to add the part.';
            if (apiData?.validData?.error) msg = apiData.validData.error;
            else if (apiData?.message) msg = apiData.message;
            else if (typeof apiData === 'string') msg = apiData;
            setSubmitError(msg);
        } finally {
            setSaving(false);
        }
    };

    const fieldClass = (base, field) => (fieldErrors[field] ? `${base} !border-red-400` : base);
    const selectError = (field) => (fieldErrors[field] ? {
        ...selectPortalProps.styles,
        control: (base, state) => ({ ...selectPortalProps.styles.control(base, state), borderColor: '#f87171' })
    } : selectPortalProps.styles);

    return (
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-labelledby="part-form-title">
            <div className="absolute inset-0 bg-black/40" onClick={() => !saving && onClose()} />
            <form
                onSubmit={handleSubmit}
                noValidate
                className="relative flex flex-col w-full sm:max-w-2xl h-full bg-white shadow-2xl"
            >
                <header className="flex items-start justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-200">
                    <div className="min-w-0">
                        <h2 id="part-form-title" className="text-lg font-bold text-[#2a2e30]">
                            {editingId ? 'Edit Part' : 'Add Part'}
                        </h2>
                        {editingId && <p className="text-xs text-gray-500 font-mono truncate">{editingId}</p>}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="p-2 -mr-2 rounded-lg text-gray-500 hover:bg-gray-100"
                        aria-label="Close"
                    >
                        <FaTimes />
                    </button>
                </header>

                {showSerialsTab && (
                    <div className="flex border-b border-gray-200 px-4 sm:px-6" role="tablist">
                        {[
                            { key: 'details', label: 'Details' },
                            { key: 'serials', label: form.serials_pending ? 'Serial Numbers (missing)' : `Serial Numbers (${formatQty(form.quantity)})` }
                        ].map(({ key, label }) => (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={activeTab === key}
                                onClick={() => setActiveTab(key)}
                                className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${activeTab === key
                                    ? 'border-[#FF5733] text-[#FF5733]'
                                    : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                )}

                {showSerialsTab && activeTab === 'serials' ? (
                    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
                        <PartSerialsPanel
                            partId={editingId}
                            quantity={form.quantity}
                            pending={form.serials_pending}
                            onPendingChange={(pending) => {
                                setForm((prev) => ({ ...prev, serials_pending: pending }));
                                onSerialsChanged?.();
                            }}
                        />
                    </div>
                ) : (
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-8">
                    <Section title="Details">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>Part Name *</label>
                                <input className={fieldClass(inputClass, 'part_name')} value={form.part_name} onChange={(e) => setField('part_name', e.target.value.toUpperCase())} placeholder="e.g. BATTERY N70" />
                                <FieldError message={fieldErrors.part_name} />
                            </div>
                            <div>
                                <label className={labelClass}>Part Number *</label>
                                <input className={`${fieldClass(inputClass, 'part_number')} font-mono`} value={form.part_number} onChange={(e) => setField('part_number', e.target.value.toUpperCase())} placeholder="e.g. N70ZL" />
                                <FieldError message={fieldErrors.part_number} />
                            </div>
                            <div>
                                <label className={labelClass}>Manufacturer *</label>
                                <Select
                                    {...selectPortalProps}
                                    styles={selectError('manufacturer')}
                                    options={manufacturers.map((m) => ({ value: m.name, label: m.name }))}
                                    value={form.manufacturer ? { value: form.manufacturer, label: form.manufacturer } : null}
                                    onChange={(option) => setField('manufacturer', option?.value || '')}
                                    placeholder="Select manufacturer"
                                    isClearable
                                />
                                <FieldError message={fieldErrors.manufacturer} />
                            </div>
                            <div>
                                <label className={labelClass}>Category *</label>
                                <Select
                                    {...selectPortalProps}
                                    styles={selectError('category')}
                                    options={categories.map((c) => ({ value: c.name, label: c.name }))}
                                    value={form.category ? { value: form.category, label: form.category } : null}
                                    onChange={(option) => setField('category', option?.value || '')}
                                    placeholder="Select category"
                                    isClearable
                                />
                                <FieldError message={fieldErrors.category} />
                            </div>
                            <div>
                                <label className={labelClass}>Status</label>
                                <Select
                                    {...selectPortalProps}
                                    options={statuses.map((s) => ({ value: s.name, label: s.name }))}
                                    value={form.status ? { value: form.status, label: form.status } : null}
                                    onChange={(option) => setField('status', option?.value || 'ACTIVE')}
                                    placeholder="Select status"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Image URL</label>
                                <input className={inputClass} value={form.image_url} onChange={(e) => setField('image_url', e.target.value)} placeholder="https://..." />
                            </div>
                        </div>
                    </Section>

                    <Section
                        title="Pricing"
                        hint={`Change any value and the others update. Incl. VAT = Excl. VAT × (1 + ${vatRate}%).`}
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <label className={labelClass}>VAT Rate *</label>
                                <Select
                                    {...selectPortalProps}
                                    options={vatRates.map((v) => ({ value: v.vat_id, label: `${v.vat_name} (${v.vat_rate}%)` }))}
                                    value={selectedVat ? { value: selectedVat.vat_id, label: `${selectedVat.vat_name} (${selectedVat.vat_rate}%)` } : null}
                                    onChange={(option) => {
                                        const newVatId = option?.value || 1;
                                        const rate = Number(vatRates.find((r) => Number(r.vat_id) === Number(newVatId))?.vat_rate || 0);
                                        setForm((prev) => ({ ...prev, vat_id: newVatId, ...applyVatChange(prev, rate) }));
                                    }}
                                    placeholder="Select VAT rate"
                                />
                            </div>
                            {[
                                ['cost_excl', 'Cost (Excl. VAT) *', applyCostChange],
                                ['cost_incl', 'Cost (Incl. VAT) *', applyCostChange],
                                ['price_excl', 'Price (Excl. VAT) *', applyPriceChange],
                                ['price_incl', 'Price (Incl. VAT) *', applyPriceChange]
                            ].map(([field, label, apply]) => (
                                <div key={field}>
                                    <label className={labelClass}>{label}</label>
                                    <input
                                        type="number"
                                        inputMode="decimal"
                                        step="0.01"
                                        min="0"
                                        className={fieldClass(numberInputClass, field)}
                                        value={form[field]}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const pair = field.startsWith('cost') ? ['cost_excl', 'cost_incl'] : ['price_excl', 'price_incl'];
                                            setPricing(pair, (prev) => apply(prev, field, val, vatRate));
                                        }}
                                        placeholder="0.00"
                                    />
                                    <FieldError message={fieldErrors[field]} />
                                </div>
                            ))}
                            <div>
                                <label className={labelClass}>Margin %</label>
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    step="0.01"
                                    min="0"
                                    max="99.99"
                                    className={fieldClass(numberInputClass, 'margin_percent')}
                                    value={form.margin_percent}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setPricing(['margin_percent', 'markup_percent', 'price_excl', 'price_incl'], (prev) => applyMarginChange(prev, val, vatRate));
                                    }}
                                />
                                <FieldError message={fieldErrors.margin_percent} />
                            </div>
                            <div>
                                <label className={labelClass}>Markup %</label>
                                <input
                                    type="number"
                                    inputMode="decimal"
                                    step="0.01"
                                    min="0"
                                    className={fieldClass(numberInputClass, 'markup_percent')}
                                    value={form.markup_percent}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setPricing(['margin_percent', 'markup_percent', 'price_excl', 'price_incl'], (prev) => applyMarkupChange(prev, val, vatRate));
                                    }}
                                />
                                <FieldError message={fieldErrors.markup_percent} />
                            </div>
                        </div>
                        {isMoney(form.cost_incl) && isMoney(form.price_incl) && (
                            <p className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                                Profit per unit: <span className="font-semibold">{formatMoney(Number(form.price_incl) - Number(form.cost_incl))}</span> (incl. VAT)
                            </p>
                        )}
                    </Section>

                    <Section title="Stock" hint="Quantity on hand changes only through parts transactions (purchases, transfers, adjustments, job cards).">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <label className={labelClass}>On Hand</label>
                                <input className={numberInputClass} value={formatQty(form.quantity)} readOnly disabled />
                            </div>
                            <div>
                                <label className={labelClass}>Min Quantity</label>
                                <input type="number" inputMode="decimal" min="0" step="0.01" className={fieldClass(numberInputClass, 'min_quantity')} value={form.min_quantity} onChange={(e) => setField('min_quantity', e.target.value)} />
                                <FieldError message={fieldErrors.min_quantity} />
                            </div>
                            <div>
                                <label className={labelClass}>Max Quantity</label>
                                <input type="number" inputMode="decimal" min="0" step="0.01" className={fieldClass(numberInputClass, 'max_quantity')} value={form.max_quantity} onChange={(e) => setField('max_quantity', e.target.value)} />
                                <FieldError message={fieldErrors.max_quantity} />
                            </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="mt-1 h-4 w-4 accent-[#FF5733]"
                                    checked={form.has_serial}
                                    disabled={serialLocked}
                                    onChange={(e) => {
                                        setForm((prev) => ({ ...prev, has_serial: e.target.checked, serials: [] }));
                                        setFieldErrors((prev) => ({ ...prev, serials: undefined }));
                                    }}
                                />
                                <span>
                                    <span className="block text-sm font-medium text-gray-800">Track serial numbers</span>
                                    <span className="block text-xs text-gray-500">
                                        {serialLocked
                                            ? 'Can’t be turned off while serial-tracked units are in stock.'
                                            : 'Each unit gets a serial when received, moved or fitted (e.g. batteries).'}
                                    </span>
                                </span>
                            </label>
                            {needsOpeningSerials && (
                                <div>
                                    <SerialNumbersField
                                        mode="enter"
                                        allowFewer
                                        quantity={form.quantity}
                                        value={form.serials}
                                        onChange={(serials) => {
                                            setForm((prev) => ({ ...prev, serials }));
                                            setFieldErrors((prev) => ({ ...prev, serials: undefined }));
                                        }}
                                        label={`Serial numbers for the ${formatQty(form.quantity)} unit(s) on hand`}
                                    />
                                    <FieldError message={fieldErrors.serials} />
                                    {missingOpeningSerials > 0 && !fieldErrors.serials && (
                                        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                            Don&apos;t have them all yet? You can save now. The {formatQty(missingOpeningSerials)} unit(s) without a serial
                                            will block this part from purchases, adjustments, transfers and job cards until they are added on its
                                            Serial Numbers tab.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </Section>

                    <Section title="Compatible Vehicles">
                        <CompatibleVehiclesEditor vehicles={vehicles} onChange={setVehicles} vehicleMakes={vehicleMakes} />
                        <div>
                            <label className={labelClass}>Compatibility Notes</label>
                            <TextareaAutosize
                                minRows={2}
                                maxRows={8}
                                className={textareaClass}
                                value={form.compatibility_notes}
                                onChange={(e) => setField('compatibility_notes', e.target.value)}
                                placeholder="e.g. Fits models with the 1.5L engine only"
                            />
                        </div>
                    </Section>

                    <Section title="Notes">
                        <div>
                            <label className={labelClass}>Internal Notes</label>
                            <TextareaAutosize
                                minRows={2}
                                maxRows={8}
                                className={textareaClass}
                                value={form.notes}
                                onChange={(e) => setField('notes', e.target.value)}
                                placeholder="Anything else worth knowing about this part"
                            />
                        </div>
                    </Section>
                </div>
                )}

                <footer className="border-t border-gray-200 px-4 sm:px-6 py-3 bg-white space-y-2">
                    {submitError && <p className="text-sm text-red-500">{submitError}</p>}
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className="h-10 px-5 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="h-10 px-5 rounded-lg bg-[#FF5733] text-white text-sm font-semibold hover:bg-[#ff4520] disabled:opacity-60 inline-flex items-center justify-center gap-2"
                        >
                            {saving && <FaSpinner className="animate-spin" />}
                            {editingId ? 'Save Changes' : 'Add Part'}
                        </button>
                    </div>
                </footer>
            </form>
        </div>
    );
}
